import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import OperatorMessage from 'app/components/TextMessage/OperatorMessage'
import UserMessage from 'app/components/TextMessage/UserMessage'
import { fetchMoreMessagesStart } from 'app/redux/messages/messages.actions'
import StatusMessage from 'app/components/StatusMessage'
import { setHeaderExpand } from 'app/redux/widget/widget.actions'
import DynamicChatForm from '../../DynamicChatForm'
import DateDivider from './components/dateDivider'
import clsx from 'clsx'
import DragAndDropFile from 'app/containers/widget/DragAndDropFile'
import LogoBar from './components/logoBar'
import './messages.scss'
import FeedbackBox from 'app/components/FeedbackBox'
import { areInTheSameDay } from 'app/utils/date'
import AiThinkingIndicator from 'app/containers/widget/launcher/firstChat/messages/components/aiThinkingIndicator'

const getHeaderExpand = ({ widgetData }) => widgetData.widgetInfo.headerExpand
const getDepartmentsData = ({ messagesData }) => messagesData.departments.data
const getActiveDepartment = ({ messagesData }) => messagesData.activeDepartment.data
const getHideRaychatLogo = ({ widgetData }) => widgetData.widgetInfo.data.publicSetting.hideRaychatLogoBar
const getUploadConfirmData = ({ messagesData }) => messagesData.messages.confirmUploadedData
const getDisableInput = ({ messagesData }) => messagesData.messages.disableInput

const Messages = ({ messages }) => {
  const [fetchedOldMessages, setFetchedOldMessages] = useState(false)
  const [forcedFormComponent, setForcedFormComponent] = useState(null)

  const forcedFormRef = useRef(null)

  const dispatch = useDispatch()
  const headerExpand = useSelector(getHeaderExpand)
  const departmentsData = useSelector(getDepartmentsData)
  const activeDepartment = useSelector(getActiveDepartment)
  const hideRaychatLogo = useSelector(getHideRaychatLogo)
  const raychatMessagesContainerElement = useRef(null)
  const confirmUploadedData = useSelector(getUploadConfirmData)
  const disableInput = useSelector(getDisableInput)

  const shouldRenderLogoBar = !hideRaychatLogo
  const forcedFormUnfilled = messages.some((item) => item.formStatus === 'unfilled' && item.fillingFormIsForced)
  const department = departmentsData?.find((item) => item._id === activeDepartment)
  const { allFetched, page } = department?.msgLoadDetails || {}

  const chatInputDisabled = disableInput.status

  // checks if prev message is from operator and if so, it wont show avatar because the same person is continuing the conversation
  const shouldShowOperatorAvatar = (currentMsgIndex) => {
    const currentMsg = messages[currentMsgIndex]
    const prevMsg = messages[currentMsgIndex - 1]
    const isFirstMsg = !prevMsg
    const isPrevMessageFromVisitor = prevMsg?.me || prevMsg?.me === null

    const bothFromOperatorButNotInTheSameDay =
      !isPrevMessageFromVisitor &&
      currentMsg?.createdAt &&
      prevMsg?.createdAt &&
      !areInTheSameDay(currentMsg.createdAt, prevMsg.createdAt)

    const notFromTheSameOperator = currentMsg?.author !== prevMsg?.author

    return isFirstMsg || isPrevMessageFromVisitor || notFromTheSameOperator || bothFromOperatorButNotInTheSameDay
  }

  const isAiThinking = useMemo(() => {
    const isLastMessageFromUser = messages.length > 0 && !!messages[messages.length - 1]?.me

    const isPreviousMessageFromAiBot = messages.length > 1 && !!messages[messages.length - 2]?.isAiBotMsg

    const isPreviousMessageBotMessage = messages.length > 1 && messages[messages.length - 2]?.type === 'bot_message'

    const isMessageBeforePreviousFromAiBot = messages.length > 2 && !!messages[messages.length - 3]?.isAiBotMsg

    // AI is thinking if:
    // 1. Last message is from user AND previous message is from AI bot
    // OR
    // 2. Last message is from user AND previous message is a bot message AND the message before that is from AI bot
    return (
      (isLastMessageFromUser && isPreviousMessageFromAiBot) ||
      (isLastMessageFromUser && isPreviousMessageBotMessage && isMessageBeforePreviousFromAiBot)
    )
  }, [messages])

  const getOperatorMessage = (message, index) => {
    const messageType = message.type

    if (messageType === 'dynamicForm') {
      const element = (
        <DynamicChatForm
          setForcedFormComponent={setForcedFormComponent}
          ref={forcedFormRef}
          selectedDepartment={activeDepartment}
          message={message}
        />
      )
      if (forcedFormUnfilled && forcedFormComponent) {
        return <></>
      }

      if (forcedFormUnfilled && !forcedFormComponent) {
        setForcedFormComponent({ component: element, done: false })
        return <></>
      }

      return element
    }

    if (messageType === 'feedback') {
      return (
        <div>
          <FeedbackBox
            rate={message.rate}
            comment={message.comment}
            teamId={message.teamId}
            agentId={message.agentId}
            conversationId={message.conversationId}
            status={message.formStatus}
          />
        </div>
      )
    }

    return (
      <OperatorMessage
        conversationId={message.conversationId}
        messageType={message.type}
        createdAt={message.createdAt}
        messageContent={message.content}
        messageId={message.id}
        botId={message.botId}
        messageReplyId={message.replyId}
        forcedFormUnfilled={forcedFormUnfilled}
        messageAuthor={message.author}
        autoMsgSender={message.sender}
        sendAfterFormCompletion={message.sendAfterForm}
        isMessageEdited={message.edited}
        messageTeamId={message.teamId}
        botMessageLevel={message.botMessageLevel}
        tmpId={message.tmpId}
        isMsgAutoMsg={message.isAutoMessage}
        fileUrl={message.url}
        fileName={message.name}
        fileData={message.fileData}
        fileSize={message.size}
        withAvatar={shouldShowOperatorAvatar(index)}
        hideConnectWithOperatorBtn={message.hideConnectWithOperatorBtn}
        isAiBotMsg={message.isAiBotMsg}
        isLastMsg={index === messages.length - 1}
      />
    )
  }

  const getChatMessage = (message, index) => {
    if (message.me) {
      return (
        // Passed all needed data explicitly in props for memoizing component correctly.
        <UserMessage
          messageId={message.id}
          tmpId={message.tmpId}
          messageType={message.type}
          messageContent={message.content}
          createdAt={message.createdAt}
          fileUrl={message.url}
          name={message.name}
          size={message.size}
          fileData={message.fileData}
          messageStatus={message.status}
          progress={message.progress}
          messageReplyId={message.replyId}
          botId={message.botId}
        />
      )
    } else if (message.me === false) {
      return getOperatorMessage(message, index)
    } else if (message.me === null) {
      return (
        <StatusMessage
          messageType={message.type}
          operatorName={message.agent?.name}
          operatorAvatar={message.agent?.avatar}
          operatorId={message.agent?.id}
        />
      )
    }
  }

  const handleScroll = (e) => {
    const element = e.target

    if (element.scrollTop === 0 && element.clientHeight < element.scrollHeight) {
      dispatch(setHeaderExpand(true))
    } else {
      if (headerExpand) dispatch(setHeaderExpand(false))
    }
  }

  // renders list of messages and adds date divider if neccessary
  const renderMessagesList = () => {
    let lastDateInIso = undefined

    return messages.map((message, index) => {
      const msgDate = message.createdAt || new Date()
      const key = message.id || index

      let dateInIso = new Date(msgDate).toISOString().slice(0, 10)
      const areDatesDifferent = lastDateInIso !== dateInIso

      if (areDatesDifferent) lastDateInIso = dateInIso

      return (
        <React.Fragment key={key}>
          {areDatesDifferent && <DateDivider date={msgDate} />}
          {getChatMessage(message, index)}
        </React.Fragment>
      )
    })
  }

  useEffect(() => {
    const element = raychatMessagesContainerElement.current

    if (fetchedOldMessages) {
      element.scrollTop = 50
      setFetchedOldMessages(false)
      return
    }

    if (element && !fetchedOldMessages) {
      element.scrollTop = element.scrollHeight - element.clientHeight
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages?.length])

  useEffect(() => {
    if (headerExpand && !allFetched) {
      dispatch(fetchMoreMessagesStart(page, messages[0]?.conversationId, activeDepartment))
      setFetchedOldMessages(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerExpand])

  return (
    <div
      data-shadowed={!!confirmUploadedData}
      onScroll={handleScroll}
      id="raychat_messages_container"
      className="messages_container"
      ref={raychatMessagesContainerElement}
    >
      {shouldRenderLogoBar && <LogoBar />}
      {!chatInputDisabled && <DragAndDropFile />}
      <div
        style={
          forcedFormComponent && !forcedFormComponent?.done && forcedFormUnfilled
            ? {
                maxHeight: `calc(100% - ${forcedFormRef.current?.clientHeight}px)`,
                overflow: 'scroll',
              }
            : {}
        }
        className={clsx(shouldRenderLogoBar && 'inner_messages_with_logo_bar', 'inner_messages_container')}
      >
        {renderMessagesList()}
        {isAiThinking && <AiThinkingIndicator />}
      </div>
      {forcedFormComponent && !forcedFormComponent?.done && forcedFormComponent.component}
    </div>
  )
}

export default React.memo(Messages)
